/**
 * The global strip: it sits ABOVE the Stage and the Rail. It slides down over
 * 180 ms.
 *
 * TWO faces left (F10, fix I reduced this from four): registration never got
 * an ack · we are reconnecting. "Opened in another tab" and the refused-token
 * face moved to the full-screen `overlay/ClientChanged` card (fix I) — both
 * are genuinely blocking states a one-line strip under-sold, and the old
 * `ConnectionModal` they ported from is still never reused directly. The one
 * face left with an action is drawn in the `well` tone; the passive one is
 * `evTint`.
 */
import React, {useEffect, useRef} from 'react';
import {Animated, Easing, Platform, StyleSheet} from 'react-native';

import {MOTION, RADIUS, SIZE, SPACE} from '../theme/tokens';
import {useV2Theme} from '../theme/useV2Theme';
import {Button, Txt} from '../ui';
import {StripModel} from './model';

export const STRIP_HEIGHT = SIZE.strip;

/** which of the two faces is up — the strip only ever shows one */
export type StripFace = 'registrationFailed' | 'reconnecting';

export const face = (registrationFailed: boolean): StripFace =>
  registrationFailed ? 'registrationFailed' : 'reconnecting';

/** one line per face */
export const LINE: Record<StripFace, string> = {
  registrationFailed: "Can't reach the game server",
  reconnecting: 'Reconnecting…',
};

export default React.memo(function StripView({
  registrationFailed,
  visible,
  onReconnect,
}: StripModel) {
  const tokens = useV2Theme(s => s.tokens);
  const drop = useRef(new Animated.Value(-STRIP_HEIGHT)).current;

  useEffect(() => {
    const slide = Animated.timing(drop, {
      toValue: visible ? 0 : -STRIP_HEIGHT,
      duration: MOTION.ms,
      easing: Easing.bezier(0.2, 0.8, 0.3, 1),
      useNativeDriver: Platform.OS !== 'web',
    });
    slide.start();
    // an animation left running past the unmount keeps requesting frames for
    // a component that is gone (E11: it is what leaked in `body.test.tsx`)
    return () => slide.stop();
  }, [visible, drop]);

  if (!visible) {
    return null;
  }

  return (
    <Animated.View
      testID="v2-strip"
      style={[
        styles.strip,
        {
          backgroundColor: registrationFailed ? tokens.well : tokens.evTint,
          boxShadow: `inset 0 0 0 1px ${
            registrationFailed ? tokens.line2 : tokens.evLine
          }`,
          transform: [{translateY: drop}],
        } as any,
      ]}>
      <Txt variant="label">{LINE[face(registrationFailed)]}</Txt>
      {registrationFailed && (
        <Button
          label="Reconnect"
          variant="primaryFlat"
          size="sm"
          onPress={onReconnect}
        />
      )}
    </Animated.View>
  );
});

const styles = StyleSheet.create({
  strip: {
    height: STRIP_HEIGHT,
    borderRadius: RADIUS.panel,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACE.md,
    paddingHorizontal: 14,
  },
});
